let applicableChecks = [];
let shippingCost = [];
let requiredFields = [];

(function ($) {
  "use strict";
  initQuantityListener();
  initShippingChangeListener();
  initPromocodeListener();
})(jQuery);

/**
 * Adds a product to the shopping cart.
 * This function is a wrapper around cartChangeInternal that specifically handles
 * the add operation for cart items.
 *
 * @param {string|number} id - The unique identifier of the product to add to the cart
 * @returns {jqXHR} jQuery AJAX promise object representing the add request
 */
function cartAdd(id) {
  cartChangeInternal(id, "add");
}

/**
 * Updates the quantity of a product in the shopping cart.
 * This function is a wrapper around cartChangeInternal that specifically handles
 * the update operation for cart items with a specified quantity.
 *
 * @param {string|number} id - The unique identifier of the product to update in the cart
 * @param {number} quantity - The new quantity to set for the product
 * @returns {jqXHR} jQuery AJAX promise object representing the update request
 */
function cartUpdate(id, quantity) {
  cartChangeInternal(id, "update", quantity);
}

/**
 * Decreases the quantity of a product in the shopping cart by one.
 * This function is a wrapper around cartChangeInternal that specifically handles
 * the subtract operation for cart items.
 *
 * @param {string|number} id - The unique identifier of the product to subtract from the cart
 * @returns {jqXHR} jQuery AJAX promise object representing the subtract request
 */
function cartSubtract(id) {
  cartChangeInternal(id, "subtract");
}

/**
 * Removes a product from the shopping cart.
 * This function is a wrapper around cartChangeInternal that specifically handles
 * the delete operation for cart items.
 *
 * @param {string|number} id - The unique identifier of the product to remove from the cart
 * @returns {jqXHR} jQuery AJAX promise object representing the delete request
 */
function cartDelete(id) {
  cartChangeInternal(id, "delete");
}

/**
 * Performs cart operations by making AJAX requests to the server and updating the UI accordingly.
 * This internal function handles all cart modifications including adding, updating, subtracting,
 * and deleting items from the cart.
 *
 * @param {string|number} id - The product ID to perform the operation on
 * @param {string} type - The type of cart operation to perform ('add', 'update', 'subtract', or 'delete')
 * @param {number} [quantity=1] - The quantity to set or change (default is 1)
 * @returns {jqXHR} jQuery AJAX promise object representing the request
 */
function cartChangeInternal(id, type, quantity = 1) {
  return $.ajax({
    type: "GET",
    url: "/cart/" + type,
    container: true,
    data: {
      id,
      quantity,
    },
    beforeSend: function () {
      $(`[cart-control="${id}"]`).addClass("loading");
    },
    success: function (response) {
      if (response.productCount > 0) {
        $(`[cart-control="${id}"]`).addClass("added");
      } else {
        $(`[cart-control="${id}"]`).removeClass("added");
      }
      $(`[cart-control="${id}"] input`).val(response.productCount);
      $(`[product-cost-id="${id}"]`).each(function () {
        $(this).text(
          formatAsCurrency(
            parseFloat($(this).attr("product-price")) * response.productCount
          )
        );
      });
      $(".cart-count").text(response.cartCount);
      $(".cart-total").text(formatAsCurrency(response.cartTotal));
      $("#cart-total").data("total", response.cartTotal);
      ensureApplicableShippingMethod();
      renewShippingParameters();
    },
    complete: function () {
      $(`[cart-control="${id}"]`).removeClass("loading");
    },
    error: function (xhr, status, error) {
      console.error(`Cart ${type} operation failed:`, error);
    },
  });
}

/**
 * Initializes event listeners for quantity input fields in the shopping cart.
 * This function finds all input elements within cart-control containers and
 * attaches change event handlers to update the cart when the quantity changes.
 *
 * The function works by:
 * 1. Selecting all input elements within cart-control containers
 * 2. For each input, extracting the product ID from its parent container
 * 3. Attaching a change event handler that calls cartUpdate with the product ID and new quantity
 *
 * @returns {void} This function does not return a value
 */
function initQuantityListener() {
  $(`[cart-control] input`).each(function (index, element) {
    const $this = $(element);
    const id = $this.parents("[cart-control]").attr("cart-control");
    $this.on("change", function () {
      cartUpdate(id, $this.val());
    });
  });
}

/**
 * Initializes an event listener for shipping method changes in the cart form.
 * This function attaches a change event handler to the shipping method selection
 * element that triggers the renewShippingParameters function whenever the user
 * selects a different shipping option.
 *
 * @returns {void} This function does not return a value
 */
function initShippingChangeListener() {
  $(`#cartform-shippingmethodid`).on("change", renewShippingParameters);
}

/**
 * Updates the shipping parameters in the cart based on the selected shipping method.
 * This function calculates shipping costs, updates the total order amount, and manages
 * the visibility and requirement status of address-related form fields based on the
 * selected shipping method's requirements.
 *
 * The function performs the following operations:
 * 1. Gets the currently selected shipping method
 * 2. Calculates the shipping cost based on the cart total
 * 3. Determines which address fields are required for the selected method
 * 4. Updates the UI to show/hide and mark as required the appropriate fields
 * 5. Updates the displayed shipping cost and order total
 *
 * @returns {void} This function does not return a value
 */
function renewShippingParameters() {
  const newValue = $('input[id*="-shippingmethodid-"]:checked').val();
  const cartTotal = $("#cart-total").data("total");
  const cost = (shippingCost[newValue] ?? (() => 0))(cartTotal);
  const total = cartTotal + cost;
  const neededAddressFields = requiredFields[newValue] ?? [];

  const fieldsConfig = {
    localityId: {
      selector: "#cartform-localityid",
      fieldClass: ".field-cartform-localityid",
    },
    address: {
      selector: "#cartform-address",
      fieldClass: ".field-cartform-address",
    },
  };

  Object.entries(fieldsConfig).forEach(([field, config]) => {
    const isRequired = neededAddressFields.includes(field);
    $(config.selector).prop("disabled", !isRequired);
    $(config.fieldClass)
      .toggleClass("d-none", !isRequired)
      .toggleClass("required", isRequired);
    $(`${config.fieldClass} label`).toggleClass("has-star", isRequired);
  });
  $("#shipping-cost").text(formatAsCurrency(cost));
  $("#order-total").text(formatAsCurrency(total));
}

/**
 * Ensures that only applicable shipping methods are available based on the cart total.
 * This function validates the currently selected shipping method against the cart total
 * and automatically selects an alternative method if the current one becomes invalid.
 *
 * The function performs the following operations:
 * 1. Gets the currently selected shipping method
 * 2. Iterates through all shipping methods and disables those that are not applicable
 *    based on the current cart total using the applicableChecks functions
 * 3. If the currently selected method is no longer applicable, it:
 *    a. Unchecks the current method
 *    b. Finds the first applicable method and selects it
 *    c. If no applicable methods exist, updates shipping parameters without a method
 *
 * @returns {void} This function does not return a value
 */
function ensureApplicableShippingMethod() {
  const $currentMethodInput = $('input[id*="-shippingmethodid-"]:checked');
  const currentMethod = $currentMethodInput.val();
  const cartTotal = $("#cart-total").data("total");

  // Disable shipping methods that are not applicable based on cart total
  $('input[id*="-shippingmethodid-"]').each((index, element) => {
    const isApplicable = (applicableChecks[element.value] || (() => false))(
      cartTotal
    );
    $(element).prop("disabled", !isApplicable);
  });

  // If current method is disabled, find a new applicable method
  if (currentMethod && $currentMethodInput.prop("disabled")) {
    const newMethod = Object.keys(applicableChecks).find((key) =>
      applicableChecks[key](cartTotal)
    );

    $currentMethodInput.prop("checked", false);

    if (newMethod !== undefined) {
      $(`input[id*="-shippingmethodid-"][value="${newMethod}"]`)
        .prop("checked", true)
        .trigger("change");
    } else {
      renewShippingParameters();
    }
  }
}

/**
 * Checks if the cart form contains validation errors.
 * This function examines the cart form for elements marked with aria-invalid="true"
 * which indicates form fields that have failed validation.
 *
 * @returns {boolean} Returns true if the cart form contains validation errors,
 *                    false otherwise
 */
function isCartFormHasErrors() {
  return $("#cart-form").find('[aria-invalid="true"]').length > 0;
}

/**
 * Initializes an event listener for the promocode application button.
 * This function attaches a click event handler to the promocode button that sends
 * the entered promocode to the server for validation and application to the cart.
 * If the server returns a falsy response (indicating an invalid code), the input
 * field is cleared.
 *
 * @returns {void} This function does not return a value
 */
function initPromocodeListener() {
  $("#applyPromocode").on("click", function () {
    const $input = $(this).parent().find("input");
    const code = $input.val();
    if (code.length) {
      const csrfParam = window.yii.getCsrfParam();
      const csrfToken = window.yii.getCsrfToken();
      $.ajax({
        url: "/cart/use-promocode",
        data: {
          [csrfParam]: csrfToken,
          code,
        },
        type: "POST",
        success: function (response) {
          if (!response) {
            $input.val("");
          }
        },
      });
    }
  });
}
